import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import InputAdornment from "@mui/material/InputAdornment";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

const MONTH_VIEW = "month";
const DAY_VIEW = "day";
const YEAR_VIEW = "year";
const DATE_FORMAT = "MM/DD/YYYY";
const DATE_INPUT_FORMATS = [
  DATE_FORMAT,
  "YYYY-MM-DD",
  "YYYY-MM-DDTHH:mm:ss Z",
  "YYYY-MM-DD HH:mm:ss Z",
  "YYYY-MM-DD HH:mm:ss UTC",
];

const styles = {
  button: {
    px: 0,
    py: 0.5,
    borderRadius: 5,
  },
};

const getButtonStyle = isSelected => ({
  color: isSelected ? "white" : "black",
  backgroundColor: isSelected ? "primary.main" : "transparent",
  "&:hover": {
    backgroundColor: isSelected ? "primary.dark" : "action.hover",
  },
});

const getDaysInMonth = (month, year) => moment(`${year}-${month}`, "YYYY-MM").daysInMonth();

const parseDateValue = value => {
  if (!value || value === "N/A") return null;

  const strictDate = moment(value, DATE_INPUT_FORMATS, true);
  if (strictDate.isValid()) return strictDate;

  const fallbackDate = moment(value);
  return fallbackDate.isValid() ? fallbackDate : null;
};

export default function CommonMonthDayYearPicker({
  value,
  onChange,
  disabled,
  label = "Date (MM/DD/YYYY)",
  errorText = "",
  autoOpen = false,
}) {
  const containerRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleOpen = event => setAnchorEl(event.currentTarget);
  const handleClose = () => {
    setAnchorEl(null);
    setViewMode(MONTH_VIEW);
  };

  const [viewMode, setViewMode] = useState(MONTH_VIEW);

  const initial = useMemo(() => {
    if (value === "N/A") return { month: "", day: "", year: moment().year() };
    const m = parseDateValue(value);
    if (!m || !m.isValid()) return { month: "", day: "", year: moment().year() };
    return {
      month: m.format("MM"),
      day: m.format("DD"),
      year: parseInt(m.format("YYYY"), 10),
    };
  }, [value]);

  const [selectedMonth, setSelectedMonth] = useState(initial.month);
  const [selectedDay, setSelectedDay] = useState(initial.day);
  const [selectedYear, setSelectedYear] = useState(initial.year);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);

  useEffect(() => {
    setSelectedMonth(initial.month);
    setSelectedDay(initial.day);
    setSelectedYear(initial.year);
  }, [initial]);

  useEffect(() => {
    if (autoOpen && !hasAutoOpened && containerRef.current && !open && !disabled) {
      setAnchorEl(containerRef.current);
      setHasAutoOpened(true);
    }
  }, [autoOpen, hasAutoOpened, open, disabled]);

  const handleMonthSelect = month => {
    setSelectedMonth(month);
    setViewMode(DAY_VIEW);
  };

  const handleDaySelect = day => {
    setSelectedDay(day);
    setViewMode(YEAR_VIEW);
  };

  const finalize = (month, day, year) => {
    const formatted = `${month}/${day}/${year}`;
    onChange && onChange(formatted);
    handleClose();
  };

  const handleYearSelect = year => {
    setSelectedYear(year);
    finalize(selectedMonth, selectedDay, year);
  };

  const handleNASelect = () => {
    onChange && onChange("N/A");
    handleClose();
  };

  const getDisplayValue = v => {
    if (v === "N/A") return "N/A";
    if (!v) return "";
    const date = parseDateValue(v);
    return date ? date.format(DATE_FORMAT) : v;
  };

  const goBack = () => {
    if (viewMode === DAY_VIEW) setViewMode(MONTH_VIEW);
    else if (viewMode === YEAR_VIEW) setViewMode(DAY_VIEW);
  };

  const renderContent = () => {
    if (viewMode === MONTH_VIEW) {
      return (
        <Box>
          <Grid container spacing={1}>
            <Grid size={12}>
              <Typography fontWeight={500}>Select Month</Typography>
            </Grid>
            {Array.from({ length: 12 }, (_, i) => {
              const monthNum = String(i + 1).padStart(2, "0");
              const monthName = moment()
                .month(i)
                .format("MMM");
              const isSelected = selectedMonth === monthNum;
              return (
                <Grid key={i} size={4}>
                  <Button
                    fullWidth
                    onClick={() => handleMonthSelect(monthNum)}
                    sx={{ ...styles.button, ...getButtonStyle(isSelected) }}
                  >
                    {`${monthName} (${monthNum})`}
                  </Button>
                </Grid>
              );
            })}
            <Grid sx={{ mt: 1, display: "flex", justifyContent: "center" }} size={12}>
              <Button
                variant={value === "N/A" ? "contained" : "text"}
                onClick={handleNASelect}
                sx={{ ...styles.button, ...getButtonStyle(value === "N/A") }}
              >
                N/A
              </Button>
            </Grid>
          </Grid>
        </Box>
      );
    } else if (viewMode === DAY_VIEW) {
      const daysInMonth = selectedMonth ? getDaysInMonth(selectedMonth, selectedYear) : 31;
      const monthName = selectedMonth
        ? moment()
            .month(parseInt(selectedMonth) - 1)
            .format("MMMM")
        : "";
      return (
        <Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Button startIcon={<ArrowBackIcon />} onClick={goBack} size="small">
              Back
            </Button>
            <Typography fontWeight={500} sx={{ ml: 2 }}>
              {monthName} ({selectedMonth})
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {Array.from({ length: daysInMonth }, (_, i) => {
              const dayNum = String(i + 1).padStart(2, "0");
              const isSelected = selectedDay === dayNum;
              return (
                <Grid key={i} size={3}>
                  <Button
                    fullWidth
                    onClick={() => handleDaySelect(dayNum)}
                    sx={{ ...styles.button, ...getButtonStyle(isSelected) }}
                  >
                    {dayNum}
                  </Button>
                </Grid>
              );
            })}
            <Grid display="flex" justifyContent="center" size={12}>
              <Button
                variant={value === "N/A" ? "contained" : "text"}
                onClick={handleNASelect}
                sx={{ ...styles.button, ...getButtonStyle(value === "N/A") }}
              >
                N/A
              </Button>
            </Grid>
          </Grid>
        </Box>
      );
    } else {
      const startYear = 2020;
      const years = Array.from({ length: 31 }, (_, i) => (startYear + i).toString());
      return (
        <Box>
          <Box display="flex" alignItems="center" mb={1}>
            <Button startIcon={<ArrowBackIcon />} onClick={goBack} size="small">
              Back
            </Button>
            <Typography fontWeight={500} sx={{ ml: 2 }}>
              {selectedMonth}/{selectedDay}
            </Typography>
          </Box>
          <Grid container spacing={1}>
            {years.map(year => (
              <Grid key={year} size={4}>
                <Button
                  fullWidth
                  onClick={() => handleYearSelect(year)}
                  sx={{ ...styles.button, ...getButtonStyle(false) }}
                >
                  {year}
                </Button>
              </Grid>
            ))}
            <Grid display="flex" justifyContent="center" size={12}>
              <Button
                variant={value === "N/A" ? "contained" : "text"}
                onClick={handleNASelect}
                sx={{ ...styles.button, ...getButtonStyle(value === "N/A") }}
              >
                N/A
              </Button>
            </Grid>
          </Grid>
        </Box>
      );
    }
  };

  return (
    <div ref={containerRef}>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={getDisplayValue(value)}
        error={!!errorText}
        helperText={errorText}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <InsertInvitationIcon color={disabled ? "disabled" : "action"} />
            </InputAdornment>
          ),
        }}
        disabled={disabled}
        onClick={disabled ? undefined : handleOpen}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 280 }}>{renderContent()}</Box>
      </Popover>
    </div>
  );
}
